import { buildReducer } from '../../utils/redux/getReduxModuleByFeatureName';
import getConstantsByFeatureName from '../../utils/redux/getConstantsByFeatureName';
import getActionCreators from '../../utils/redux/getActionCreators';
import getActionHandlers from '../../utils/redux/getActionHandlers';
import getSelectorByFeatureName from '../../utils/redux/getSelectorByFeatureName';
import getCRUDSagas from '../../utils/redux/getCRUDSagas';
import { initialState as commonInitialState } from '../../utils/redux/commonActionHandlers';
import Api from '../../utils/Api';
import { get } from 'lodash';


const constantsPrefix = 'USER-EXPERIENCES';
const statePropName = 'userExperiences';
const formStatePropName = 'userExperiences';
const formName = 'experience';
const mainPageUrl = '/users/:userId/experiences';

// QA 4.2 round 4 (2026-05-22): pagination on the seafarer-card
// Experience tab is server-side — each page is a separate GET
// /users/:id/expiriences?page[size]=N&page[number]=N call. The
// FE selector below only re-orders the 15 rows it gets; it can't
// fix WHICH 15 rows the BE returns for page 1. Round 1-3 fixes
// were within-page only.
//
// Without a `sort=` query param, the BE's order is effectively
// random (id ASC on JR 0.10 + MySQL DISTINCT-strips-ORDER-BY trap;
// see expirience_resource.rb BE-side comment). So page 1 ends up
// with the oldest 15 contracts (lowest IDs), page 2 with the
// newest. Within each page, the selector re-orders DESC by date,
// but the page split itself is wrong.
//
// Cleanest fix that doesn't depend on the BE state: pin a default
// sort on the FE feature so every request includes
// `?sort=-date_on`. JR's `apply_single_sort` then adds date_on
// to the SELECT-DISTINCT list AND emits an explicit ORDER BY,
// which MySQL respects. Works on any BE version (legacy /
// patched / docker'd). Column-header sorting still works — the
// reducer's `sortItems` handler swaps direction or replaces the
// field on click.
const initialState = {
  ...commonInitialState,
  sort: { field: 'date_on', direction: 'desc' },
};

const constants = getConstantsByFeatureName(constantsPrefix);
const actions = getActionCreators(constants);
const actionHandlers = getActionHandlers(constants);
const reducer = buildReducer(initialState, {
  ...actionHandlers,
  [constants.CLEAR_FILTER]: (state) => ({ ...state, filter: { ...initialState.filter } }),
});
// Default-newest-first ordering at the selector layer. QA bug 4.2
// (2026-05-20): the BE serves `/users/:id/expiriences` through
// JR 0.10's `find_related_monomorphic_fragments`, which `.distinct`-
// plucks across the join and re-fetches by id — the model's
// `default_scope -> { order('date_on DESC') }` is lost on the way
// out. Apply the order on the FE so the seafarer card always
// shows the most-recent contract on top. Honoured by a tiny
// wrapper around the base selector; explicit user-clicked column
// sorts (`state.userExperiences.sort` set) skip the override.
//
// Date format coming back from the BE is `DD.MM.YYYY` (set by
// `ExpirienceResource#date_on` / `#date_off`). Parse to a real
// number for the comparator so 01.01.2025 > 01.01.2018 etc. —
// pure string compare on "DD.MM.YYYY" doesn't work because the
// day component sorts before the year.
const parseDateOnSerial = (s) => {
  if (!s || typeof s !== 'string') return -Infinity;
  const [d, m, y] = s.split('.').map(Number);
  if (!d || !m || !y) return -Infinity;
  return y * 10000 + m * 100 + d;
};

// QA 4.2 round 2 (2026-05-21): the original implementation
// read `row.date_on` / `row.date_off` directly. But the saga
// commits items in JSON:API shape (`{ id, type, attributes: {
// date_on, date_off, ... } }`), so the top-level access was
// always undefined — every item tied at -Infinity and the
// sort was a no-op in prod. (The round-1 spec used a flat
// shape so it passed CI but the prod sort never happened.)
// Read through `attributes` first, fall back to the top-level
// field so legacy tests/code paths that use the flat shape
// still work.
const fieldOf = (row, key) =>
  get(row, ['attributes', key]) ?? row[key];

const baseSelector = getSelectorByFeatureName(statePropName);
const selector = (state) => {
  const slice = baseSelector(state);
  // Only enforce the default order when the user hasn't set an
  // explicit sort — in that case the saga re-queried the BE with
  // `?sort=...` and we forward its result untouched.
  if (slice.sort && slice.sort.field) return slice;
  if (!Array.isArray(slice.data)) return slice;

  const data = [...slice.data].sort((a, b) => {
    const onA = parseDateOnSerial(fieldOf(a, 'date_on'));
    const onB = parseDateOnSerial(fieldOf(b, 'date_on'));
    if (onA !== onB) return onB - onA;
    return parseDateOnSerial(fieldOf(b, 'date_off'))
      - parseDateOnSerial(fieldOf(a, 'date_off'));
  });
  return { ...slice, data };
};


const sagas = getCRUDSagas(
  mainPageUrl,
  Api.userExperiences,
  actions,
  constants,
  formStatePropName,
  formName,
  { itemName: 'Experience' },
);

export { constants, actions, reducer, selector, sagas, formName };
